/**
 * 仿真会话驱动（设计文档 session 层；M1 起主线程 RAF 实现，Worker 延后到按需 / v2.0 第 2 批）。
 *
 * 用 requestAnimationFrame 按倍速推进内核虚拟时钟：每帧 stepUntil 到
 * baseSimMs + (now-baseWall)×speed；节流推送快照给画布（命令式绘制，绕过 React），
 * ~4Hz 推送指标给仪表盘。控制：start/pause/resume/step/setSpeed/reset/updateParams。
 *
 * 内核本身不使用真实时间——确定性结果不受驱动节拍影响（AC-F006-01/AC-F013）。
 */
import { Simulation, type MetaEntry } from '../core/engine/simulation';
import type { SimEvent } from '../core/engine/events';
import type { ExperimentConfig } from '../core/config-schema';
import type { DispatchStrategy, StrategyParams } from '../strategy/strategy-api';
import type { SimulationSnapshot } from '../core/snapshot';
import type { MetricsSummary, SeriesPoint } from '../core/metrics/collector';
import type { KinematicsParams } from '../core/physics/kinematics';

export type SimSessionState = 'idle' | 'running' | 'paused' | 'finished';

export interface SimSessionCallbacks {
  /** 每帧（含运行段插值所需的视觉仿真时刻 visualSimMs）。 */
  onFrame?: (snapshot: SimulationSnapshot, visualSimMs: number) => void;
  /** 指标刷新（~4Hz，或暂停/步进/结束时强制）。 */
  onMetrics?: (summary: MetricsSummary) => void;
  onStateChange?: (state: SimSessionState) => void;
  onFinish?: (summary: MetricsSummary) => void;
}

const METRICS_INTERVAL_MS = 250;
const SNAPSHOT_THROTTLE_MS = 33; // 快照构建节流（~30Hz）；画布在两次快照间靠运动学插值保持连续

export class SimSession {
  private readonly sim: Simulation;
  private readonly cb: SimSessionCallbacks;
  private state: SimSessionState = 'idle';
  private speed = 1;
  private raf = 0;
  private baseSimMs = 0;
  private baseWallMs = 0;
  private lastMetricsWallMs = 0;
  private cachedSnapshot: SimulationSnapshot | null = null;
  private lastSnapshotWallMs = 0;

  constructor(
    config: ExperimentConfig,
    makeStrategy: () => DispatchStrategy,
    callbacks: SimSessionCallbacks = {},
  ) {
    this.cb = callbacks;
    this.sim = new Simulation();
    this.sim.init(config, makeStrategy, { keepLog: true }); // 交互/导出路径需完整事件日志
  }

  get currentState(): SimSessionState {
    return this.state;
  }
  get currentSpeed(): number {
    return this.speed;
  }
  get kinematics(): KinematicsParams {
    return this.sim.kinematicsParams;
  }
  get snapshot(): SimulationSnapshot {
    return this.sim.buildSnapshot();
  }
  get eventLog(): readonly SimEvent[] {
    return this.sim.eventLog;
  }
  get metaLog(): readonly MetaEntry[] {
    return this.sim.metaLog;
  }
  get metricsSeries(): readonly SeriesPoint[] {
    return this.sim.metricsSeries;
  }
  get config(): ExperimentConfig {
    return this.sim.experimentConfig;
  }

  /** 运行中热切换策略（F-010）。 */
  switchStrategy(makeStrategy: () => DispatchStrategy): void {
    this.sim.switchStrategy(makeStrategy);
  }

  start(): void {
    if (this.state === 'running' || this.state === 'finished') return;
    this.state = 'running';
    this.resetClockBase();
    this.loop();
    this.emitState();
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    cancelAnimationFrame(this.raf);
    this.pushFrame(this.sim.clock.now());
    this.pushMetrics();
    this.emitState();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.resetClockBase();
    this.loop();
    this.emitState();
  }

  /** 单步：处理一条事件（暂停态可用，AC-F013-02）。 */
  step(): void {
    if (this.state === 'running' || this.state === 'finished') return;
    const hadMore = this.sim.step();
    this.pushFrame(this.sim.clock.now());
    this.pushMetrics();
    if (!hadMore) this.finish();
    else this.emitState();
  }

  setSpeed(speed: number): void {
    this.speed = speed;
    if (this.state === 'running') this.resetClockBase(); // 以当前仿真时刻为新基准，平滑变速
  }

  /** 重置到初始状态（同种子可重跑出相同结果，AC-F013-03）。 */
  reset(): void {
    cancelAnimationFrame(this.raf);
    this.sim.reset();
    this.baseSimMs = 0;
    this.baseWallMs = 0;
    this.state = 'idle';
    this.pushFrame(0);
    this.pushMetrics();
    this.emitState();
  }

  /** 实时更新策略参数（F-009：下一次派梯即生效）。 */
  updateParams(params: StrategyParams): void {
    this.sim.applyStrategyParams(params);
  }

  terminate(): void {
    cancelAnimationFrame(this.raf);
  }

  private resetClockBase(): void {
    this.baseSimMs = this.sim.clock.now();
    this.baseWallMs = performance.now();
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const now = performance.now();
    const targetSimMs = this.baseSimMs + (now - this.baseWallMs) * this.speed;
    const { done } = this.sim.stepUntil(targetSimMs);
    // 节流构建快照（~30Hz）；画布在两次快照间靠运动学插值保持连续
    if (!this.cachedSnapshot || now - this.lastSnapshotWallMs >= SNAPSHOT_THROTTLE_MS) {
      this.cachedSnapshot = this.sim.buildSnapshot();
      this.lastSnapshotWallMs = now;
    }
    this.cb.onFrame?.(this.cachedSnapshot, targetSimMs);
    if (now - this.lastMetricsWallMs >= METRICS_INTERVAL_MS) this.pushMetrics();
    if (done) this.finish();
  };

  private pushFrame(visualSimMs: number): void {
    // 单步/暂停/重置等低频路径：直接构建新快照并刷新缓存
    this.cachedSnapshot = this.sim.buildSnapshot();
    this.lastSnapshotWallMs = performance.now();
    this.cb.onFrame?.(this.cachedSnapshot, visualSimMs);
  }

  private pushMetrics(): void {
    this.lastMetricsWallMs = performance.now();
    this.cb.onMetrics?.(this.sim.metrics.summary());
  }

  private finish(): void {
    if (this.state === 'finished') return;
    cancelAnimationFrame(this.raf);
    this.state = 'finished';
    this.sim.metrics.setSimEnd(this.sim.clock.now());
    const summary = this.sim.metrics.summary();
    this.pushFrame(this.sim.clock.now());
    this.cb.onMetrics?.(summary);
    this.emitState();
    this.cb.onFinish?.(summary);
  }

  private emitState(): void {
    this.cb.onStateChange?.(this.state);
  }
}
